import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth, useLocalStorage } from '@spotify/confidence-core-react';
import { useFeatureFlag } from '@spotify/confidence-flag-evaluation-react';

import { useCurrentUser } from '../useCurrentUser';
import { useCurrentUserGroups } from '../useCurrentUserGroups';

type OwnerSelection = {
  scope: string;
  value: string | null;
};

export const useDefaultOwner = () => {
  const { user, orgName } = useAuth();
  const accountScope = user?.account ?? orgName;
  const principal = user?.sub;
  const hasStorageScope = Boolean(accountScope && principal);
  const storageKey = hasStorageScope
    ? `default-owner-v3:${accountScope}:${principal}`
    : 'default-owner-v3:unscoped';
  const { enabled: groupsOnly } = useFeatureFlag('owner-groups-only', {
    enabled: false,
  });
  const [savedOwner, setSavedOwner] = useLocalStorage<string | null>(
    storageKey,
    null,
    { migrateKey: false },
  );
  const validSavedOwner =
    hasStorageScope && (!groupsOnly || savedOwner?.startsWith('identities/g'))
      ? savedOwner
      : null;
  const scope = `${storageKey}:${groupsOnly}`;
  const [selection, setSelection] = useState<OwnerSelection | null>(null);
  const selectedOwner =
    selection?.scope === scope ? selection.value : validSavedOwner;
  const owner =
    groupsOnly && selectedOwner && !selectedOwner.startsWith('identities/g')
      ? null
      : selectedOwner;
  const initializedScope = useRef<string | null>(null);
  const shouldLoadFallback =
    owner === null && initializedScope.current !== scope;

  const { currentUser, isLoading: loadingUser } = useCurrentUser({
    skip: groupsOnly || !shouldLoadFallback,
  });
  const { identities: groups, isLoading: loadingGroups } = useCurrentUserGroups(
    {
      skip: !groupsOnly || !shouldLoadFallback,
    },
  );
  const loading =
    shouldLoadFallback && (groupsOnly ? loadingGroups : loadingUser);

  const setOwner = useCallback(
    (value: string | null) => {
      setSelection({ scope, value });
      initializedScope.current = scope;
      if (hasStorageScope && value !== null) {
        setSavedOwner(value);
      }
    },
    [scope, hasStorageScope, setSavedOwner],
  );

  useEffect(() => {
    if (!shouldLoadFallback || loading) return;
    initializedScope.current = scope;
    let fallbackOwner = currentUser?.identity.name;
    if (groupsOnly) {
      fallbackOwner = groups.length === 1 ? groups[0].name : undefined;
    }
    if (fallbackOwner) setOwner(fallbackOwner);
  }, [
    shouldLoadFallback,
    loading,
    scope,
    groupsOnly,
    groups,
    currentUser,
    setOwner,
  ]);

  return [owner, setOwner, { loading }] as const;
};
